import React, {useMemo} from 'react';
import {Box} from 'src/design/atoms/Box';
import {Progress} from 'src/design/atoms/Progress';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {CourseAssignmentCoursePivotItem} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ComplianceByCoursePivotProps {
  rows: CourseAssignmentCoursePivotItem[];
  isLoading: boolean;
}

/**
 * By-course pivot for the Compliance Cockpit: completion %, overdue count
 * and first-time/renewal split (`isRenewal` aggregated per row per Wave 1
 * spec). Links out to the existing course workspace's Assign tab — no
 * write actions this wave.
 */
export const ComplianceByCoursePivot: React.FC<ComplianceByCoursePivotProps> = ({
  rows,
  isLoading,
}) => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<CourseAssignmentCoursePivotItem>[]>(
    () => [
      {accessorKey: 'courseDefinitionName', header: 'Course'},
      {accessorKey: 'assignmentCount', header: '# Assigned'},
      {
        accessorKey: 'completionPercent',
        header: 'Completion',
        size: 160,
        Cell: ({cell}) => (
          <Box w="100%">
            <Progress value={cell.getValue<number>()} size="sm" mb={2} />
            {cell.getValue<number>().toFixed(0)}%
          </Box>
        ),
      },
      {
        accessorKey: 'overdueCount',
        header: '# Overdue',
        Cell: ({cell}) => {
          const value = cell.getValue<number>();
          return <Box c={value > 0 ? 'red' : undefined}>{value}</Box>;
        },
      },
      {accessorKey: 'firstTimeCount', header: 'First-time'},
      {accessorKey: 'renewalCount', header: 'Renewal'},
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-compliance-by-course',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'overdueCount', desc: true}],
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: rows,
      getRowId: row => String(row.courseDefinitionId),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            tooltip="View Course"
            onClick={() =>
              navigate(
                PathUtility.TrainingCourseView(
                  String(row.original.courseDefinitionId),
                  'assign'
                )
              )
            }
          />
        </TableRowActions>
      ),
    },
    totalCount: rows.length,
    isError: false,
    isLoading,
    isFetching: isLoading,
  });

  return <>{table}</>;
};
